import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, Button, Modal } from 'react-bootstrap';
import { FaMapMarkerAlt, FaEnvelope, FaPhone, FaUser } from 'react-icons/fa';
import { FaPenToSquare } from "react-icons/fa6";
import { MdPermDeviceInformation } from "react-icons/md";
import { FaTrashAlt } from "react-icons/fa";
import { getContactByClientId,getContratsByClientId, createContract,getContractTypes,updateContract ,deleteClient, deleteContract } from '../../../api/CLIENTS/clients';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import EditClient from '../../Modals/Update/EditClient';
import EditContratClient from '../../Modals/Update/EditContratCliet';
import ClientFiles from './ClientFiles';
import MarkdownRenderer from '../../MarkdownEditor/MarkdownRenderer';

const COLLAPSED_TEXT_MAX_HEIGHT_REM = 7;

// Collapsible markdown block
const ExpandableText = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    const measureOverflow = () => {
      const el = contentRef.current;
      if (!el) {
        setCanExpand(false);
        return;
      }

      const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const maxHeightPx = COLLAPSED_TEXT_MAX_HEIGHT_REM * rootFontSize;
      setCanExpand(el.scrollHeight > maxHeightPx + 1);
    };

    measureOverflow();
    window.addEventListener('resize', measureOverflow);
    return () => window.removeEventListener('resize', measureOverflow);
  }, [text]);

  if (!text?.trim()) return <p className="mb-4 text-muted fst-italic">Pas d'information</p>;

  return (
    <div className="mb-4">
      <div
        ref={contentRef}
        style={!expanded && canExpand ? { maxHeight: `${COLLAPSED_TEXT_MAX_HEIGHT_REM}rem`, overflow: "hidden", position: "relative" } : {}}
      >
        <MarkdownRenderer content={text} />
        {!expanded && canExpand && (
          <div
            style={{
              position: "absolute", bottom: 0, left: 0, right: 0,
              height: "2.5rem",
              background: "linear-gradient(to bottom, transparent, var(--bs-body-bg))",
            }}
          />
        )}
      </div>
      {canExpand && (
        <Button variant="link" size="sm" className="ps-0" onClick={() => setExpanded(!expanded)}>
          {expanded ? 'Afficher moins' : 'Afficher plus'}
        </Button>
      )}
    </div>
  );
};

const InformationsGeneralesTab = ({ client }) => {
const { hasPermission } = useAuth();
const [contrat, setContrat] = useState(null);
const [loadingContrat, setLoadingContrat] = useState(true);
const [showEdit, setShowEdit] = useState(false);
const [showEditContrat, setShowEditContrat] = useState(false);
const [contacts, setContacts] = useState([]);
const [loadingContacts, setLoadingContacts] = useState(true);
const [deleting, setDeleting] = useState(false);
const isActive = Number(client.is_active) === 1;
const navigate = useNavigate();
const [showDeleteModal, setShowDeleteModal] = useState(false);
const [contractTypes, setContractTypes] = useState([]);
const [showDeleteContractModal, setShowDeleteContractModal] = useState(false);

// Fetch all contacts associated with the current client
useEffect(() => {
  const fetchContact = async () => {
    if (client && client.id) {
      setLoadingContacts(true);
      try {
        const contactData = await getContactByClientId(client.id);
        const payload = contactData?.data ?? contactData;
        // Normalize the response to always be an array
        const normalized = Array.isArray(payload?.contacts)
          ? payload.contacts
          : Array.isArray(payload)
          ? payload
          : [];
        setContacts(normalized);
      } catch (error) {
        console.error("Erreur lors de la récupération du contact :", error);
        setContacts([]);
      } finally {
        setLoadingContacts(false);
      }
    } else {
      setContacts([]);
      setLoadingContacts(false);
    }
  };

  fetchContact();
}, [client]);

// Fetch contracts for the current client
useEffect(() => {
    const fetchContrat = async () => {
      if (!client?.id) return;

      setLoadingContrat(true);
      try {
        const res = await getContratsByClientId(client.id);
        setContrat(res?.data ?? null);
      } catch (err) {
        console.error("Erreur contrat :", err);
        setContrat(null);
      } finally {
        setLoadingContrat(false);
      }
    };

    fetchContrat();
  }, [client?.id]);

  // Fetch available contract types for the dropdown in the modal
  useEffect(() => {
  const fetchTypes = async () => {
    try {
      const res = await getContractTypes();
      setContractTypes(res?.data || []);
    } catch (err) {
      console.error("Erreur types contrat:", err);
      setContractTypes([]);
    }
  };

  fetchTypes();
}, []);

  // Format contact type for display
  const formatContactType = (type) => {
    if (!type) return 'Non renseigné';
    const t = String(type).toLowerCase();
    if (t === 'billing') return 'Facturation';
    if (t === 'contact') return 'Contact';
    return type;
  };

  // Normalize the contract response to an array and pick the first contract to display
  const contracts = Array.isArray(contrat) ? contrat : [];
  const hasContracts = contracts.length > 0;
  const contract = hasContracts ? contracts[0] : null;

  const contractTypeName = contract?.type?.name || "Pas de contrat";

  // Format the expiry date for display, falling back to "Indéterminée" if null
  const expireDate = contract?.expire_date
    ? new Date(contract.expire_date).toLocaleDateString('fr-FR')
    : "Indéterminée";

  // Delete the current contract and refresh the contract list
  const handleDeleteContract = async () => {
    if (!contract?.id) return;

    try {
      await deleteContract(client.id, contract.id);

      // Refresh contracts after deletion
      const res = await getContratsByClientId(client.id);
      setContrat(res?.data || []);

      setShowDeleteContractModal(false);

    } catch (err) {
      console.error(err?.message || "Erreur lors de la suppression");
    }
  };

  // Create or update a contract, then refresh the contract list
  const handleSubmitContract = async (contractData) => {
    try {
      const payload = {
        contrat: {
          type: Number(contractData.type),
          // Use "date_expiration" key — ContractDto::setContract() reads $data['date_expiration']
          // Send plain "YYYY-MM-DD" string — backend handles DateTime conversion internally
          date_expiration: contractData.date_expiration ?? null,
          information: contractData.information || null
        }
      };

      if (contract?.id) {
        // Update existing contract
        await updateContract(client.id, contract.id, payload);
      } else {
        // Create new contract
        await createContract(client.id, payload);
      }

      // Refresh contracts after save
      const res = await getContratsByClientId(client.id);
      setContrat(res?.data || []);

    } catch (err) {
      console.error(err?.message || "Erreur lors de la sauvegarde du contrat");
      // Re-throw so the modal can catch it and show a toast error
      throw err;
    }
  };

  // Delete the client and redirect to the clients list
  const handleDeleteClient = async () => {
    if (!client?.id) return;

    setDeleting(true);
    try {
      await deleteClient(client.id);
      navigate('/clients');
      setTimeout(() => window.location.reload(), 50);
    } catch (err) {
      console.error("Erreur suppression client:", err);
    } finally {
      setDeleting(false);
    }
  };

  if (!client) return <div>Aucune information disponible</div>;

  return (
    <>
      <Row>
        {/* ── Client information card ── */}
        <Col xl={8}>
          <Card className="border-left-primary shadow mt-3">
            <Card.Header className="d-flex flex-row align-items-center justify-content-between">
              <h6 className="text-primary fw-semibold m-0">Information client</h6>
              {hasPermission('EDIT_CLIENT') && (
                <Button variant="link" size="sm" className="text-primary" disabled={!isActive} onClick={() => setShowEdit(true)}>
                  <FaPenToSquare/>
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {/* Address, Email, Phone row */}
              <Row className="mb-4">
                <Col md={4}>
                  <div className="d-flex align-items-start">
                    <FaMapMarkerAlt className="text-muted mt-1 me-2" size={16} />
                    <div>
                      <div className="text-muted small">Adresse</div>
                      <div>{client.address || 'Non renseigné'}</div>
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="d-flex align-items-start">
                    <FaEnvelope className="text-muted mt-1 me-2" size={16} />
                    <div>
                      <div className="text-muted small">Email</div>
                      <a href={`mailto:${client.email}`}>{client.email || 'Non renseigné'}</a>
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="d-flex align-items-start">
                    <FaPhone className="text-muted mt-1 me-2" size={16} />
                    <div>
                      <div className="text-muted small">Téléphone</div>
                      <a href={`tel:${client.phone_number}`}>{client.phone_number || 'Non renseigné'}</a>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Additional info and alert banner */}
              {(client.information || client.alert) && (
                <Row className="mb-2">
                  <Col md={12}>
                    <div className="text-muted small mb-2">Informations complémentaires</div>
                    <ExpandableText text={client.information} />

                    <div className="text-muted small mb-2">Alerte</div>
                    {client.alert ? (
                      <div className="alert alert-danger mb-0 py-2">
                        <MarkdownRenderer content={client.alert} />
                      </div>
                    ) : (
                      <div className="text-muted fst-italic">Pas d'alerte</div>
                    )}
                  </Col>
                </Row>
              )}

              {/* Contacts section — only rendered when contacts exist */}
              {(!loadingContacts && Array.isArray(contacts) && contacts.length > 0) && (
                  <>
                    <hr />
                    <h6 className="text-muted mb-3">Contact</h6>
                    {contacts.map((contact, index) => (
                    <Row key={contact?.id ?? index} className="mb-3 align-items-start">
                      <Col xs={3}>
                        <div className="d-flex align-items-start">
                          <FaUser className="text-muted mt-1 me-2 flex-shrink-0" size={16} />
                          <div>
                            <div className="text-muted small">Nom</div>
                            <div>{contact.name || 'Non renseigné'}</div>
                          </div>
                        </div>
                      </Col>
                      <Col xs={3}>
                        <div className="d-flex align-items-start">
                          <FaEnvelope className="text-muted mt-1 me-2 flex-shrink-0" size={16} />
                          <div>
                            <div className="text-muted small">Email</div>
                            <a href={`mailto:${contact.email}`}>{contact.email || 'Non renseigné'}</a>
                          </div>
                        </div>
                      </Col>
                      <Col xs={3}>
                        <div className="d-flex align-items-start">
                          <FaPhone className="text-muted mt-1 me-2 flex-shrink-0" size={16} />
                          <div>
                            <div className="text-muted small">Téléphone</div>
                            <a href={`tel:${contact.phone_number}`}>{contact.phone_number || 'Non renseigné'}</a>
                          </div>
                        </div>
                      </Col>
                      <Col xs={3}>
                        <div className="d-flex align-items-start">
                          <MdPermDeviceInformation className="text-muted mt-1 me-2 flex-shrink-0" size={16} />
                          <div>
                            <div className="text-muted small">Type</div>
                            <div>{formatContactType(contact.type)}</div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  ))}
                  </>
                )}
            </Card.Body>
          </Card>
        </Col>

        {/* ── Contract and Files column ── */}
        <Col xl={4}>
          {/* Contract card */}
          <Card className="border-left-primary shadow mt-3">
            <Card.Header className="d-flex flex-row align-items-center justify-content-between">
              <h6 className="text-primary fw-semibold m-0">Contrat</h6>
              {/* Show edit button only when a contract already exists */}
              {hasPermission('EDIT_CLIENT') && hasContracts && (
                <div className="dropdown no-arrow">
                  <Button
                    variant="link"
                    size="sm"
                    className="text-primary"
                    disabled={!isActive}
                    onClick={() => setShowEditContrat(true)}
                  >
                    <FaPenToSquare />
                  </Button>
                  <div className="dropdown-menu dropdown-menu-right shadow">
                    <a className="dropdown-item" href="/">Modifier</a>
                    <a className="dropdown-item text-danger" href="/">Supprimer le contrat</a>
                  </div>
                </div>
              )}
            </Card.Header>
            <Card.Body>
              {loadingContrat ? (
                <div className="text-muted">Chargement...</div>
              ) : (
                <>
                  {hasContracts ? (
                    <>
                      <div className="mb-3">
                        <small className="text-muted d-block">Type de contrat</small>
                        <span>{contractTypeName}</span>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted d-block">Date de fin</small>
                        <span>{expireDate}</span>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted d-block">Informations complémentaires</small>
                        {contract?.information
                          ? <MarkdownRenderer content={contract.information} />
                          : <span className="text-muted fst-italic">Non renseigné</span>}
                      </div>
                    </>
                  ) : (
                    // No contract yet — show "Add contract" button
                    <div className="text-center">
                      <p className="text-muted">Pas de contrat</p>
                      {hasPermission('EDIT_CLIENT') && (
                        <Button variant="primary" size="sm" disabled={!isActive} onClick={() => setShowEditContrat(true)}>
                          Ajouter un contrat
                        </Button>
                      )}
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>

          {/* Files card */}
          <Card className="border-left-primary shadow mt-3">
            <Card.Header className="d-flex flex-row align-items-center justify-content-between">
              <div>
                <h6 className="text-primary fw-semibold m-0">Fichier</h6>
                <small className="text-muted">(.png, .jpg, .jpeg, .pdf, .csv, .txt, .avi, .webp, .mp4)</small>
              </div>
            </Card.Header>
            <Card.Body className="py-3">
            <ClientFiles
              clientId={client.id}
              canUpload={isActive && hasPermission('EDIT_CLIENT')}
              canDelete={isActive && hasPermission('EDIT_CLIENT')}
            />
          </Card.Body>
          </Card>

          {/* Delete client button — only shown to users with DELETE_CLIENT permission */}
          {hasPermission('DELETE_CLIENT') && (
            <div className="mt-3 text-end">
              <Button
                variant="danger"
                size="md"
                className="w-60 d-inline-flex align-items-center justify-content-center"
                onClick={() => setShowDeleteModal(true)}
                disabled={deleting || !isActive}
              >
                <FaTrashAlt style={{ verticalAlign: 'middle', marginRight: 8 }} />
                <span style={{ verticalAlign: 'middle' }}>
                  {deleting ? 'Suppression...' : 'Supprimer le client'}
                </span>
              </Button>
            </div>
          )}
        </Col>
      </Row>

      {/* ── Edit client modal ── */}
      <EditClient
        show={showEdit}
        onClose={() => setShowEdit(false)}
        client={client}
      />

      {/* ── Edit / create contract modal ── */}
      <EditContratClient
        show={showEditContrat}
        onClose={() => setShowEditContrat(false)}
        contrat={contract}
        contractTypes={contractTypes}
        onSubmit={handleSubmitContract}
        // Pass delete handler; modal will call this when the user confirms deletion
        onDelete={() => deleteContract(client.id, contract?.id)}
      />

      {/* ── Delete client confirmation modal ── */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmation</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>Êtes-vous sûr de vouloir supprimer ce client ?</p>
          <p className="text-danger mb-0">Cette action est irréversible.</p>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={deleting}
          >
            Annuler
          </Button>

          <Button
            variant="danger"
            onClick={handleDeleteClient}
            disabled={deleting}
          >
            {deleting ? "Suppression..." : "Oui, supprimer"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Delete contract confirmation modal ── */}
      <Modal
        show={showDeleteContractModal}
        onHide={() => setShowDeleteContractModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Supprimer le contrat</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>Êtes-vous sûr de vouloir supprimer ce contrat ?</p>
          <p className="text-danger">Cette action est irréversible.</p>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteContractModal(false)}
          >
            Annuler
          </Button>

          <Button
            variant="danger"
            onClick={handleDeleteContract}
          >
            Supprimer
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default InformationsGeneralesTab;